import { RowNode } from 'ag-grid-community';
import { checkSlots } from '../../shared/slot-utils';
import { CustomElement } from './shared-types';
import { KatDataGrid } from './data-grid';
import getString from './strings';

class GridFilterModule {
  grid: KatDataGrid;
  externalFilterStore: Record<string, any> = {};

  constructor(grid: KatDataGrid) {
    this.grid = grid;
  }

  applyCurrentFilterInStore() {
    if (Object.keys(this.externalFilterStore).length) {
      this.grid.gridOptions.api.setIsExternalFilterPresent(() => true);
    } else {
      this.grid.gridOptions.api.setIsExternalFilterPresent(() => false);
      this.grid.gridOptions.api.onFilterChanged();

      return;
    }
    if (this.grid.clientSideData) {
      const currentFunction = (node: RowNode) => {
        let filterPass = true;
        for (const filterName in this.externalFilterStore) {
          if (this.externalFilterStore[filterName]) {
            filterPass =
              filterPass && this.externalFilterStore[filterName](node);
          }
        }
        return filterPass;
      };
      this.grid.gridOptions.api.setDoesExternalFilterPass(currentFunction);
    }
    // For serverside Later changing key before calling the server
    this.grid.gridOptions.api.onFilterChanged();
    if (!this.grid.clientSideData) {
      this.grid.gridOptions.api.refreshServerSide();
    }
    this.grid._customFilterChanged.emit({
      'applied-filters': Object.keys(this.externalFilterStore),
    });
  }

  clearCustomFilters = (filterNames: string[]) => {
    filterNames.forEach(filterName => {
      if (
        !filterName ||
        !Object.prototype.hasOwnProperty.call(
          this.externalFilterStore,
          filterName
        )
      ) {
        console.warn(`${filterName} : does not exists on applied filters`);
      }
      delete this.externalFilterStore[filterName];
    });
    this.applyCurrentFilterInStore();
  };

  // clear single filter provided a name
  clearCustomFilter = (name: string) => {
    if (
      !name ||
      !Object.prototype.hasOwnProperty.call(this.externalFilterStore, name)
    ) {
      console.warn('Please provide a filter name to remove');
      return;
    }
    delete this.externalFilterStore[name];
    this.applyCurrentFilterInStore();
  };

  handleSearch = e => {
    this.grid.searchTerm = e.detail.searchTerm;
    if (this.grid.clientSideData) {
      // clientside
      if (e.detail.dropDownOption) {
        //setting selectedSearchOption which will impact getQuickFilterText to search on specific column
        this.grid.selectedSearchOption = e.detail.dropDownOption;
        this.grid.gridOptions.api.setQuickFilter(e.detail.searchTerm);
        this.grid.gridOptions.api.onFilterChanged();
      } else {
        this.grid.selectedSearchOption = undefined;
        this.grid.gridOptions.api.setQuickFilter(e.detail.searchTerm);
      }
    } else {
      //serverside
      this.grid.selectedSearchOption = e.detail.dropDownOption;
      this.grid.gridOptions.api.refreshServerSide();
      this.grid.gridOptions.api.onFilterChanged();
    }
  };

  handleClearAllFilterAndSearch = () => {
    this.grid.selectedSearchOption = undefined;
    this.grid.searchTerm = '';
    if (this.grid.clientSideData) {
      //Clearing search
      this.grid.gridOptions.api.setQuickFilter('');
      //Clearing external filter from ag grid
    }
    this.grid.serverSideSearchApplied = false;
    this.clearAllCustomFilters();
    this.grid.gridOptions.api.setFilterModel(null);
    this.grid.gridOptions.api.onFilterChanged();
    if (!this.grid.clientSideData)
      this.grid.gridOptions.api.refreshServerSide();

    // Emit to user
    this.grid._filtersCleared.emit();
  };

  isFilterApplied() {
    return (
      this.grid.gridOptions?.api?.isQuickFilterPresent() ||
      (this.grid.gridOptions.isExternalFilterPresent &&
        this.grid.gridOptions?.isExternalFilterPresent(null)) ||
      this.grid.gridOptions?.api?.isAnyFilterPresent() ||
      (!this.grid?.clientSideData && this.grid?.serverSideSearchApplied)
    );
  }

  //opens or closes filter side bar
  handleAllFilterButtonClicked = () => {
    if (this.grid.gridOptions.api.isToolPanelShowing()) {
      this.grid.gridOptions.api.closeToolPanel();
    } else {
      this.grid.gridOptions.api.openToolPanel('allFilter');
    }
  };

  handleAdvancedFilterVisual = (grid: KatDataGrid) => {
    if (!grid.advancedFilterRenderer) return;
    grid.advancedFilterRenderer.forEach(obj => {
      if ('type' in obj && obj.type === 'kat-radiobutton') {
        const radioButton = grid.shadowRoot.querySelector(
          `.advance-filter-radio-${obj.filterId} kat-radiobutton`
        );

        const radioButtonOnSideBar = this.grid.querySelector(
          `.advance-filter-radio-${obj.filterId} kat-radiobutton`
        );
        if (
          Object.prototype.hasOwnProperty.call(
            this.externalFilterStore,
            obj.filterId
          )
        ) {
          radioButton.setAttribute('checked', 'true');
          radioButtonOnSideBar?.setAttribute('checked', 'true');
        } else {
          radioButton.removeAttribute('checked');
          radioButtonOnSideBar?.removeAttribute('checked');
        }
      } else if ('type' in obj && obj.type === 'kat-dropdown') {
        const dropDownValue: string[] = [];
        obj.checkbox.forEach(filter => {
          const dropDown: CustomElement = this.grid.shadowRoot.querySelector(
            `.advance-filter-drop-down-${obj.uniqueKey} kat-dropDown`
          );
          const dropDownOnSideBar: CustomElement = this.grid.querySelector(
            `.advance-filter-drop-down-${obj.uniqueKey} kat-dropDown`
          );
          if (
            Object.prototype.hasOwnProperty.call(
              this.externalFilterStore,
              filter.filterId
            )
          ) {
            dropDownValue.push(filter.filterId);
          }
          dropDown.values = dropDownValue;

          dropDownOnSideBar && (dropDownOnSideBar.values = dropDownValue);
        });
      } else if ('type' in obj && obj.type === 'kat-checkbox') {
        const radioButton = this.grid.shadowRoot.querySelector(
          `.advance-filter-checkbox-${obj.filterId} kat-checkbox`
        );
        const radioButtonOnSideBar = this.grid.querySelector(
          `.advance-filter-checkbox-${obj.filterId} kat-checkbox`
        );
        if (
          Object.prototype.hasOwnProperty.call(
            this.externalFilterStore,
            obj.filterId
          )
        ) {
          radioButton.setAttribute('checked', 'true');
          radioButtonOnSideBar?.setAttribute('checked', 'true');
        } else {
          radioButton.removeAttribute('checked');
          radioButtonOnSideBar?.removeAttribute('checked');
        }
        //implement for cat checkbox
      }
    });
  };

  appendAdvancedFilterComponents = (
    parent: HTMLElement,
    filterClass?: string
  ) => {
    if (!parent || !this.grid?.advancedFilterRenderer) return;
    parent.innerHTML = '';
    this.grid.advancedFilterRenderer.forEach(arg => {
      const component: HTMLElement = document.createElement('div');
      if (filterClass) component.classList.add(filterClass);
      if ('type' in arg && arg.type === 'kat-radiobutton') {
        component.classList.add(`advance-filter-radio-${arg.filterId}`);
        component.innerHTML = `
            <kat-radiobutton
            style="margin-right: 16px"
            class="filter-button"
            label="${arg.label}"
          ></kat-radiobutton>`;
        component
          .querySelector('kat-radiobutton')
          .addEventListener('click', e => {
            e.preventDefault();
            arg.onClick(e);
          });
      } else if ('type' in arg && arg.type === 'kat-checkbox') {
        component.classList.add(`advance-filter-checkbox-${arg.filterId}`);
        component.innerHTML = `
            <kat-checkbox
            style="margin-right: 16px"
            class="filter-button"
            label="${arg.label}"
          ></kat-checkbox>`;
        component
          .querySelector('kat-checkbox')
          .addEventListener('change', e => {
            e.stopPropagation();
            e.preventDefault();
            arg.onClick(e);
          });
      } else if ('type' in arg && arg.type === 'kat-dropdown') {
        component.classList.add(`advance-filter-drop-down-${arg.uniqueKey}`);
        component.innerHTML = `
          <div style="margin-right: 16px;${arg.inlineStyle}"><kat-dropdown size="small" placeholder="${arg.placeholder}" multiple multiple-show-apply></kat-dropdown></div>
        `;
        const katDropDown = component.querySelector('kat-dropdown');
        katDropDown.setAttribute(
          'options',
          JSON.stringify(
            arg.checkbox.map(options => ({
              name: options.name,
              value: options.filterId,
            }))
          )
        );
        component
          .querySelector('kat-dropdown')
          .addEventListener('change', (e: any) => {
            arg.checkbox.forEach(() => {
              arg.onChange(e);
            });
          });
      } else if ('renderer' in arg) {
        component.classList.add(`advance-filter-custom-${arg.filterId}`);
        component.append(arg.renderer());
      }
      parent.append(component);
    });
  };

  appendFiltersInSideBar() {
    if (!this.grid?.advancedFilterRenderer) return;
    try {
      const existingFilters = this.grid.querySelector(
        '.additional-filters-side-bar'
      );
      if (existingFilters) {
        existingFilters.remove();
      }
      const wrapper = document.createElement('div');
      wrapper.setAttribute('class', 'additional-filters-side-bar');

      const additionalFilterHeader = document.createElement('div');
      additionalFilterHeader.setAttribute('class', 'additional-filter-header');
      additionalFilterHeader.innerText = getString('dg-additional-filter');
      wrapper.append(additionalFilterHeader);

      if (this.grid.advancedFilterRenderer) {
        this.appendAdvancedFilterComponents(
          wrapper,
          'additional-filter-side-bar'
        );
      }

      this.grid.querySelector('.ag-filter-toolpanel')?.append(wrapper);

      //appending title
      const titleContainer = document.createElement('div');
      const title = document.createElement('span');
      title.setAttribute('class', 'side-bar-title');
      title.innerText = getString('dg-all-filters');
      titleContainer.append(title);

      //appending clearAll button
      const clearAll = document.createElement('kat-link');
      clearAll.setAttribute('href', '#');
      clearAll.setAttribute('label', getString('dg-clear-filters'));
      clearAll.setAttribute('variant', 'link');
      clearAll.setAttribute('class', 'clear-all-link');
      clearAll.addEventListener('click', e => {
        e.preventDefault();
        this.handleClearAllFilterAndSearch();
      });
      titleContainer.append(clearAll);

      const agFilterToolPanelSearch = this.grid.querySelector(
        '.ag-filter-toolpanel-search'
      );
      if (agFilterToolPanelSearch) {
        agFilterToolPanelSearch.innerHTML = '';
        agFilterToolPanelSearch.append(titleContainer);
      }
    } catch (e) {
      console.error(e);
    }
  }

  isFilterBarEmpty(grid: KatDataGrid) {
    const slots = checkSlots(grid);
    return (
      !slots.filter &&
      !grid.enableSearch &&
      !grid.additionalFilter &&
      !grid.advancedFilterRenderer
    );
  }

  clearAllCustomFilters = () => {
    this.externalFilterStore = {};
    this.applyCurrentFilterInStore();
  };
}

export { GridFilterModule };
